/**
 * Application top bar.
 *
 * Per ``docs/engineering/detail/storyboards/shared/chrome-spec.md``
 * §Top bar (2026-05-26 amendment — Appaloosa-aligned aesthetic
 * refresh): 48 px tall, ``--bg-surface`` background, 1 px bottom
 * border. Contents left-to-right:
 *
 * 1. Product wordmark — the classification-specific application title
 *    (REQ-UI-060; e.g. "MERIDIAN | NIGHTCAP") in Bebas Neue 24 px,
 *    ``--brand``, read from the deployment config via
 *    :func:`useApplicationTitle`. The deployment-designation pill that
 *    used to sit beside it was removed 2026-05-29 — the classification
 *    is conveyed by the top/bottom banner (REQ-UI-058) + the title
 *    codename, so the pill was redundant.
 * 1a. Operator-configured clocks (REQ-UI-085; ADR-033 §8) — 0–4 zoned
 *    clocks rendered by :class:`Clocks` immediately right of the wordmark.
 *    Renders nothing until a clock is configured.
 * 2. Spacer.
 * 3. **System rollup badge** (REQ-STATUS-048) — the collector-computed
 *    ``overall_state`` as a status dot + state word. Replaced the
 *    client-observed LIVE / RECONNECTING connection indicator here on
 *    2026-08-01; the two SSE connection states moved to the User Interface
 *    Manager Selection Detail (REQ-STATUS-049,
 *    :file:`entity-details/status-stream-sections.tsx`) where they cannot be
 *    mistaken for a system-health statement. The badge is subject to the
 *    fail-safe ``Stale`` precedence rule — see :func:`SystemRollupBadge`.
 * 3a. Active-Profile line (item 7f, 2026-06-08) — "Profile: «name»"
 *    with a "(modified)" marker when the working state diverges, or
 *    "Custom (unsaved)" when no Profile is applied. Moved here from
 *    the gear menu so the operator sees the applied Profile without
 *    opening the menu (REQ-UI-074). Reads ``usePreferences()`` +
 *    :func:`useWorkingStateModified`.
 * 4. Current user + role pill.
 * 5. Preferences gear icon — anchors the
 *    :class:`PreferencesMenu` Radix-DropdownMenu inline-dropdown
 *    (per Flow P 2026-05-26 update; modal→dropdown refactor
 *    landed in Phase 3 of the UI refresh). The gear button
 *    + popover live inside ``PreferencesMenu`` so the
 *    trigger/popover lifecycle stays encapsulated.
 * 7. Sign-out icon.
 *
 * Display-only mode (Stream A PR 6) adds the "DISPLAY-ONLY" badge
 * between the role pill and the gear; sign-out is replaced by
 * "Exit display-only" in that mode.
 *
 * Cross-references:
 *   - REQ-STATUS-048 (system rollup badge + fail-safe ``Stale`` precedence).
 *   - REQ-STATUS-016 (never a false green); REQ-UI-046 (status palette, never
 *     colour alone); REQ-UI-132 (badge contrast).
 *   - REQ-UI-008 (live data stream indicator — now surfaced by
 *     REQ-STATUS-049's Selection Detail rows, not here).
 *   - REQ-UI-074 (active-Profile + "(modified)" marker — top-bar line).
 *   - REQ-UI-040 / 042 (theme + preferences menu surface).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Connection-state machine".
 *   - docs/engineering/detail/storyboards/flows/P-ui-preferences/README.md
 *     §"Where the gear icon lives".
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md
 *     2026-05-26 amendment (visual treatment).
 */
import { SignOut } from "@phosphor-icons/react";
import { useSession } from "@/auth/session-context";
import { Clocks } from "@/chrome/clocks";
import { ProfileSwitcher } from "@/chrome/profile-switcher";
import { useApplicationTitle } from "@/chrome/use-application-title";
import { ClassificationBadge } from "@/chrome/classification-badge";
import { useClassification } from "@/chrome/use-classification";
import { logout } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { PreferencesMenu } from "@/preferences/preferences-menu";
import { STATUS_PILL_CLASS } from "@/system-status/status-detail-primitives";
import { rollupStateMeta } from "@/system-status/status-model";
import {
  resolveSystemBadgeState,
  useSystemStatusStreamConnection,
  type SystemBadgeState,
} from "@/system-status/use-system-status-stream";

/**
 * Top-bar component. Owns the preferences-modal open state so the
 * gear button and the modal share lifecycle without leaking into
 * the surrounding layout. The modal renders inline; Radix's
 * ``Dialog.Portal`` lifts it to the document body so the topbar's
 * fixed-height shell never clips it.
 */
export function TopBar(): React.ReactElement {
  const { user } = useSession();
  const primaryRole = user.roles[0] ?? "operator";
  // Classification-specific wordmark from the deployment config
  // (REQ-UI-060); falls back to "MERIDIAN" until the config resolves.
  const title = useApplicationTitle();
  // Classification marking for the top-bar badge (REQ-UI-058, badge form
  // 2026-06-12; replaced the top/bottom classification banner). Null → fail-safe.
  const classification = useClassification();

  return (
    <header
      className="flex h-12 items-center gap-3 border-b border-meridian-topbar-border bg-meridian-topbar-bg px-4 text-text-primary"
      data-testid="top-bar"
    >
      {/* Wordmark: Bebas Neue 24px in --brand (amber dark, blue
       * light) per chrome-spec.md 2026-05-26 amendment §"Top bar
       * (production)". The text is the classification-specific
       * application title (REQ-UI-060). */}
      <span
        className="font-display text-display text-brand tracking-[0.08em] leading-none"
        data-testid="app-title"
      >
        {title}
      </span>

      {/* Classification badge — the classification marking (REQ-UI-058, badge
       * form 2026-06-12), immediately right of the wordmark. Replaced the
       * top/bottom classification banner; muted banner colours, fail-safe UNVERIFIED. */}
      <ClassificationBadge classification={classification} />

      {/* Operator-configured top-bar clocks (REQ-UI-085; ADR-033 §8),
       * immediately right of the wordmark per chrome-spec.md §Top bar.
       * Renders nothing until a clock is configured, so the default bar is
       * unchanged. */}
      <Clocks />

      <div className="flex-1" />

      <SystemRollupBadge />

      {/* Active-Profile switcher (item 3; REQ-UI-073/074) — a dropdown showing
       * the active Profile + "(modified)" marker that quick-switches Profiles.
       * Sits just left of the user block. */}
      <ProfileSwitcher />

      <div className="flex items-center gap-2" data-testid="user-block">
        <span className="text-body-sm text-text-secondary">{user.username}</span>
        <span
          className="rounded-sm bg-bg-elevated text-text-secondary px-1.5 py-0.5 text-meta font-mono uppercase tracking-wide"
          aria-label={`Role: ${primaryRole}`}
        >
          {primaryRole}
        </span>
      </div>

      <PreferencesMenu />

      <button
        type="button"
        aria-label="Sign out"
        title="Sign out"
        onClick={() => {
          void logout();
        }}
        className="rounded p-1.5 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
        data-testid="sign-out"
      >
        <SignOut size={18} />
      </button>
    </header>
  );
}

/**
 * Per-badge-state presentation: the visible state word, the dot colour, and the
 * text-colour utility for the word.
 *
 * The four rollup states reuse :func:`rollupStateMeta` verbatim so the top-bar
 * badge and the panel's :func:`StatusPill` cannot drift into naming or
 * colouring the same server-reported state differently — a "Degraded" pill in
 * the panel beside a differently-worded badge in the chrome is an operator
 * asking which one is right. ``stale`` is the one state that has no rollup
 * counterpart (it is a client-observed verdict, not a collector one) and is
 * defined here.
 *
 * Colour never carries the state alone: every badge renders its state WORD
 * (REQ-UI-046).
 */
const BADGE_META: Record<SystemBadgeState, { label: string; dotColor: string; textClass: string }> =
  {
    healthy: rollupStateMeta("healthy"),
    degraded: rollupStateMeta("degraded"),
    unhealthy: rollupStateMeta("unhealthy"),
    unknown: rollupStateMeta("unknown"),
    stale: {
      label: "Stale",
      // ``--status-stale`` — the token the panel's ``standby`` also uses, and
      // the reason this badge wears ``.status-palette``: outside that class the
      // MAP value resolves, which measures 1.32:1 on the light top bar.
      dotColor: "var(--status-stale)",
      textClass: "text-meridian-status-stale",
    },
  };

/** Hover / screen-reader explanation per badge state. */
const BADGE_TITLES: Record<SystemBadgeState, string> = {
  healthy: "System status: Healthy (reported by the status collector).",
  degraded: "System status: Degraded (reported by the status collector).",
  unhealthy: "System status: Unhealthy (reported by the status collector).",
  unknown: "System status is not yet known — no status frame has been received.",
  stale:
    "The live status feed for this browser is not connected. Showing Stale rather than the " +
    "last-received system state, which may no longer be true.",
};

/**
 * The always-visible system rollup badge (REQ-STATUS-048).
 *
 * Renders the collector-computed ``overall_state`` — **except** that it can
 * never report ``Healthy`` while this browser's status feed is dead. See
 * :func:`resolveSystemBadgeState` for the precedence rule and why it is
 * load-bearing: the slot it occupies used to report a client-observed fact
 * ("is my data flowing?"), and substituting a server-reported one without the
 * rule would turn the product's only always-visible staleness signal into a
 * reassurance that survives its own evidence.
 *
 * Reads the application-scope stream (:func:`useSystemStatusStreamConnection`),
 * not a panel-owned one — a badge in chrome outlives every panel.
 *
 * ``status-palette`` supplies the three ``StatusPill`` token corrections that
 * were previously reachable only inside ``.system-status-scope``; without it
 * this badge would resolve the map palette (see the block comment in
 * :file:`styles/globals.css` §``.status-palette``).
 *
 * # Same shape as every other rollup badge, by construction
 *
 * The pill geometry comes from the shared :data:`STATUS_PILL_CLASS` rather than
 * a copy of its class string. This badge is deliberately **not** a
 * :func:`StatusPill` — it owns ``role="status"``, ``aria-live``, a ``stale``
 * state that is not a :type:`RollupState`, and uppercase type — but "not the
 * same component" was letting it be a different *size*, which is the
 * inconsistency the architect flagged on 2026-08-01. Sharing the constant keeps
 * the two in step without collapsing the distinction (CLAUDE.md §4.4).
 *
 * Note the shared width is a **floor**: this badge's uppercase labels are wider
 * than the panel's sentence-case ones, so "UNHEALTHY" simply exceeds it while
 * the short states ("STALE") are brought up to the common size.
 *
 * Cross-references:
 *   - REQ-STATUS-048 / -016 / -014; REQ-UI-046 (never colour alone);
 *     REQ-UI-059 (accessible name); REQ-UI-132 (contrast).
 */
export function SystemRollupBadge(): React.ReactElement {
  const stream = useSystemStatusStreamConnection();
  const badgeState = resolveSystemBadgeState(stream.lastFrame?.overall_state ?? null, stream.state);
  const { label, dotColor, textClass } = BADGE_META[badgeState];
  return (
    <div
      className={cn("status-palette", STATUS_PILL_CLASS)}
      data-testid="system-rollup-badge"
      data-state={badgeState}
      data-stream-state={stream.state}
      aria-label={`System status: ${label}`}
      title={BADGE_TITLES[badgeState]}
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: dotColor }}
      />
      <span className={cn("uppercase tracking-wide", textClass)}>{label}</span>
    </div>
  );
}
